// Copyright (c) 2020 by Audere
//
// Use of this source code is governed by an LGPL-3.0 license that
// can be found in the LICENSE file distributed with this file.

import { Container, Dialog, Typography } from "@material-ui/core";
import {
  DashboardRoute,
  ROUTES,
  ROUTE_DEFINITIONS,
  getPath,
} from "../../routes/routes";
import { Link, useHistory, useLocation, useRouteMatch } from "react-router-dom";
import React, {
  ReactElement,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { Theme, createStyles, makeStyles } from "@material-ui/core/styles";
import { cx, useAnimateBgAndColor } from "style/utils";

import AppBar from "@material-ui/core/AppBar";
import CloseIcon from "@material-ui/icons/Close";
import { CustomHeaderItem } from "utils/ConfigTypes";
import { CustomerContext } from "components/App/DashboardApp";
import DeclarativeContentRenderer from "components/DeclarativeComponents/DeclarativeContentRenderer";
import Divider from "@material-ui/core/Divider";
import Drawer from "@material-ui/core/Drawer";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import HeaderLogo from "./HeaderLogo";
import Hidden from "@material-ui/core/Hidden";
import IconButton from "@material-ui/core/IconButton";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import NavBarBackButton from "./NavBarBackButton";
import SignOutButton from "../Login/SignOutButton";
import Tab from "@material-ui/core/Tab";
import Tabs from "@material-ui/core/Tabs";
import Toolbar from "@material-ui/core/Toolbar";
import { getHeaderLogoRoute } from "./getHeaderLogoRoute";
import { useAuthState } from "../../store/auth";

const useTabStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      marginRight: theme.spacing(0),
      minWidth: "100px",
      "&:hover": {
        color: "#fff",
        backgroundColor: theme.palette.primary.dark,
      },
    },
  })
);

interface LinkTabProps {
  label?: string;
  href?: string;
  className?: string;
  target?: string;
  rel?: string;
}

function LinkTab(props: LinkTabProps) {
  const history = useHistory();
  return (
    <Tab
      component="a"
      className={cx(
        useTabStyles().root,
        useAnimateBgAndColor().animateBgAndColor,
        props.className || ""
      )}
      onClick={(event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        if (props.target === "_blank") {
          return;
        }
        event.preventDefault();
        props.href && history.push(props.href);
      }}
      {...props}
    />
  );
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    list: {
      flex: 1,
    },

    listItem: {
      borderRadius: 0,
      display: "block",
      width: "100%",
    },

    menuButton: {
      marginRight: theme.spacing(2),
    },

    noop: {
      display: "none",
    },

    tabContainer: {
      flexGrow: 1,
    },

    tabContainerRight: {
      flexDirection: "row-reverse",
      "& .MuiTabs-flexContainer": {
        flexDirection: "row-reverse",
      },
    },

    centeredTitle: {
      left: "50%",
      position: "absolute",
      transform: "translate(-50%, 0)",
    },

    title: {
      color: "#fff",
      "&:hover": {
        color: "initial",
      },
    },

    logoNavDisabled: {
      cursor: "default",
      color: "#fff",
      fontWeight: 500,
    },

    sideMenuHeader: {
      display: "flex",
      lineHeight: "56px",
      textAlign: "left",
      [theme.breakpoints.up("sm")]: {
        lineHeight: "64px",
      },
    },

    sideMenuHeaderTitle: {
      flexGrow: 1,
    },

    sideMenuHeaderClose: {
      minWidth: "52px",
    },

    dialogAppBar: {
      position: "relative",
    },
    dialogContent: {
      margin: theme.spacing(3),
    },
  })
);

const Navigation = () => {
  const authState = useAuthState();
  const { auth } = authState;
  const customContext = useContext(CustomerContext)!;
  const { headerConfig } = customContext;
  const performTestRoute = ROUTE_DEFINITIONS.PERFORMTEST;
  const isInTestFlow = useRouteMatch(getPath(performTestRoute));

  // is the drawer open
  const [isOpen, setOpen] = useState(false);
  // is a dialog selected
  const [selectedDialog, setSelectedDialog] = useState<null | string>(null);

  // current tab highlighted
  const [tabIndex, setTabIndex] = React.useState<number>(0);
  const classes = useStyles();
  const location = useLocation();

  const openDrawer = useCallback(
    (event: React.KeyboardEvent | React.MouseEvent) => {
      setOpen(true);
    },
    []
  );

  const closeDrawer = useCallback(
    (event: React.KeyboardEvent | React.MouseEvent) => {
      setOpen(false);
    },
    []
  );

  // Called when a tab is clicked.
  const handleChange = useCallback(
    (event: React.ChangeEvent<{}>, newValue: number) => {
      setTabIndex(newValue);
    },
    []
  );
  let availableRoutes: Array<DashboardRoute> = [];

  // When loading the page we want to highlight the proper tab.
  useEffect(() => {
    const found = availableRoutes.find(
      (route: DashboardRoute, index: number) => {
        if (route.path.startsWith(location.pathname)) {
          setTabIndex(index + 1);
          return true;
        }
        return false;
      }
    );
    if (!found) {
      setTabIndex(0);
    }
  }, [location, availableRoutes]);

  availableRoutes = ROUTES.filter(route => {
    const shouldShow = route.renderNavTab({
      authState: authState,
      customerConfig: customContext,
    });
    if (shouldShow && !route.navTitle) {
      console.error(
        `Route ${route.path} is attempting to have an item in the nav, but doesn't declare a navTitle`
      );
      return false;
    }
    return shouldShow;
  });

  // filter authentication routes.
  if (customContext.disableLogin) {
    availableRoutes = availableRoutes.filter(route => {
      return (
        route !== ROUTE_DEFINITIONS.LOGIN && route !== ROUTE_DEFINITIONS.SIGNUP
      );
    });
  }

  // Items in the side nav, only visible in small screens.
  const listItems: Array<ReactElement> = [];

  // Items in the navbar, only visible in large screens.
  const tabs: Array<ReactElement> = [
    // This is a hidden item, it is used when there's no match on the url.
    // The <Tabs /> component needs this or it throws a warning in the console.
    <LinkTab href={""} key={"noop"} label={"noop"} className={classes.noop} />,
  ];
  let sideMenuIcon: IconProp;
  // This is not a ternary because of some limitation of typescript when it comes
  // to large type definitions.
  if (headerConfig.sideMenuIcon) {
    sideMenuIcon = headerConfig.sideMenuIcon;
  } else {
    sideMenuIcon = "cog";
  }

  // Dialogs to pop up
  const dialogs: Array<ReactElement> = [];
  const onCloseDialog = () => {
    setSelectedDialog(null);
  };

  availableRoutes.forEach((route: DashboardRoute, index: number) => {
    listItems.push(
      <ListItem
        disableGutters
        button
        className={classes.listItem}
        component={Link}
        key={"sidenav-" + route.path}
        to={getPath(route)}
      >
        {route.navTitle}
      </ListItem>
    );
    tabs.push(
      <LinkTab href={getPath(route)} key={route.path} label={route.navTitle} />
    );
  });

  if (customContext.headerConfig.headerItems) {
    customContext.headerConfig.headerItems.forEach(
      (headerItem: CustomHeaderItem) => {
        switch (headerItem.type) {
          case "link":
            listItems.push(
              <ListItem
                disableGutters
                button
                className={classes.listItem}
                component="a"
                href={headerItem.href}
                target="_blank"
                rel="noopener noreferrer"
                key={"sidenav-" + headerItem.title}
              >
                {headerItem.title}
              </ListItem>
            );
            tabs.push(
              <LinkTab
                href={headerItem.href}
                key={headerItem.shortTitle}
                label={headerItem.shortTitle}
                target="_blank"
                rel="noopener noreferrer"
              />
            );
            break;
          case "dialog":
            listItems.push(
              <ListItem
                disableGutters
                button
                className={classes.listItem}
                key={"sidenav-" + headerItem.title}
                onClick={() => {
                  setSelectedDialog(headerItem.title);
                }}
              >
                {headerItem.title}
              </ListItem>
            );
            tabs.push(
              <Tab
                key={headerItem.shortTitle}
                label={headerItem.shortTitle}
                onClick={() => {
                  setSelectedDialog(headerItem.title);
                }}
              />
            );
            dialogs[headerItem.title] = (
              <Dialog
                fullScreen
                disableBackdropClick={false}
                aria-labelledby="dialog-title"
                aria-describedby="dialog-description"
                open
                onClose={onCloseDialog}
                title={headerItem.shortTitle}
              >
                <AppBar className={classes.dialogAppBar}>
                  <Toolbar>
                    <IconButton
                      edge="start"
                      color="inherit"
                      aria-label="close"
                      onClick={onCloseDialog}
                    >
                      <CloseIcon />
                    </IconButton>
                    <Typography variant="h6" id="dialog-title">
                      {headerItem.shortTitle}
                    </Typography>
                  </Toolbar>
                </AppBar>
                <div className={classes.dialogContent}>
                  {headerItem.content && (
                    <DeclarativeContentRenderer
                      contentDescription={headerItem.content}
                    />
                  )}
                </div>
              </Dialog>
            );
            break;
        }
      }
    );
  }

  const highlightIndex = tabIndex > tabs.length - 1 ? 0 : tabIndex;

  const isLogoCentered = headerConfig.headerLogo.position === "center";

  const tabsElement = (
    <div className={cx(classes.tabContainer)}>
      <Hidden smDown>
        <Tabs
          value={highlightIndex}
          onChange={handleChange}
          aria-label="Site Navigation"
          className={cx({
            [classes.tabContainerRight]: !isLogoCentered,
          })}
        >
          {tabs}
        </Tabs>
      </Hidden>
    </div>
  );

  const logoLinkStyle: any = {};
  if (headerConfig.headerLogo.color) {
    logoLinkStyle.color = headerConfig.headerLogo.color;
  }

  const logoElement =
    headerConfig.headerLogo.disabledInTest && isInTestFlow ? (
      <div
        className={cx(
          { [classes.centeredTitle]: isLogoCentered },
          classes.logoNavDisabled,
          classes.title
        )}
        style={logoLinkStyle}
      >
        <HeaderLogo />
      </div>
    ) : (
      <Link
        className={cx(
          { [classes.centeredTitle]: isLogoCentered },
          classes.title
        )}
        style={logoLinkStyle}
        to={getHeaderLogoRoute(customContext)}
      >
        <HeaderLogo />
      </Link>
    );

  const signOutElement =
    !customContext.disableLogin && auth && !auth.isGuest ? (
      <Hidden smDown>
        <SignOutButton onClick={closeDrawer} />
      </Hidden>
    ) : null;

  const hamburgerElement = (
    <Hidden mdUp>
      <IconButton
        edge="end"
        onClick={openDrawer}
        className={classes.menuButton}
        color="inherit"
        aria-label="menu"
      >
        <FontAwesomeIcon icon={sideMenuIcon} />
      </IconButton>
    </Hidden>
  );

  let toolbarContent: ReactElement;
  if (isLogoCentered) {
    toolbarContent = (
      <>
        <NavBarBackButton />
        {tabsElement}
        {logoElement}
        {signOutElement}
        {hamburgerElement}
      </>
    );
  } else {
    toolbarContent = (
      <>
        <NavBarBackButton />
        {logoElement}
        {tabsElement}
        {signOutElement}
        {hamburgerElement}
      </>
    );
  }

  return (
    <>
      {selectedDialog && dialogs[selectedDialog]}
      <AppBar position="static" id="navbar">
        <Toolbar>{toolbarContent}</Toolbar>
      </AppBar>
      <Drawer anchor="right" open={isOpen} onClose={closeDrawer}>
        <div role="presentation" className={classes.list} onClick={closeDrawer}>
          <Container maxWidth={false}>
            <div className={classes.sideMenuHeader}>
              <div className={classes.sideMenuHeaderTitle}>
                {customContext.name}
              </div>
              <div>
                <IconButton className={classes.sideMenuHeaderClose}>
                  <FontAwesomeIcon icon="times" />
                </IconButton>
              </div>
            </div>
            <Divider />
            <List>
              {listItems}
              {!customContext.disableLogin && auth && !auth.isGuest && (
                <SignOutButton
                  className={classes.listItem}
                  onClick={closeDrawer}
                />
              )}
            </List>
          </Container>
        </div>
      </Drawer>
    </>
  );
};

export default Navigation;
